import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { Scan, ShoppingBag, CreditCard, X, Check, Trash2, Minus, Plus, Search, Camera } from 'lucide-react';
import BarcodeScanner from '../components/BarcodeScanner';
import { useAutosave, clearDraft } from '../hooks/useAutosave';
import { useServerDrafts } from '../hooks/useServerDrafts';
import { DraftsPanel } from '../components/DraftsPanel';

const TAX_RATE = 0.0725;
const POS_KEY = 'webb_pos_draft';

const POS = () => {
  const navigate = useNavigate();
  // R13: POS form starts BLANK on every visit. Server drafts loaded into DraftsPanel only.
  const [cart, setCart] = useState([]);
  const [barcode, setBarcode] = useState('');
  const [squareConfig, setSquareConfig] = useState(null);
  const [paymentReady, setPaymentReady] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [lastReceipt, setLastReceipt] = useState(null);
  const [showScanner, setShowScanner] = useState(false);
  const cardRef = useRef(null);
  const paymentsRef = useRef(null);
  const barcodeInputRef = useRef(null);
  const [productSearch, setPosProductSearch] = useState('');
  const [productResults, setPosProductResults] = useState([]);
  const [showPosProductDropdown, setShowPosProductDropdown] = useState(false);
  const posProductDropdownRef = useRef(null);
  const posSearchTimeoutRef = useRef(null);

  // R13: Server drafts hook
  const { drafts: serverDrafts, currentDraftId, saveDraft, deleteDraft, clearCurrentDraft, setCurrentDraftId } =
    useServerDrafts('pos');

  // (Local autosave kept as a UI-speed cache; server is durable store)
  useAutosave(POS_KEY, { cart, customerName });

  // Server-side debounced save on every change
  useEffect(() => {
    if (cart.length === 0 && !customerName) return;
    const total = cart.reduce((s, i) => s + (i.total || 0), 0);
    const label = `${customerName || 'Walk-in'} • ${cart.length} item${cart.length === 1 ? '' : 's'} • $${total.toFixed(2)}`;
    saveDraft({ data: { cart, customerName }, label });
  }, [cart, customerName, saveDraft]);

  const handleResumeDraft = useCallback((draft) => {
    if (!draft || !draft.data) return;
    if (draft.data.cart) setCart(draft.data.cart);
    if (draft.data.customerName !== undefined) setCustomerName(draft.data.customerName);
    setCurrentDraftId(draft.id);
  }, [setCurrentDraftId]);

  // Load Square config
  useEffect(() => {
    const loadSquare = async () => {
      try {
        const config = await api.getSquareConfig();
        setSquareConfig(config);
      } catch (err) {
        console.error('Failed to load Square config:', err);
      }
    };
    loadSquare();
  }, []);

  // Initialize Square Web Payments SDK when payment form is shown
  const initSquarePayment = useCallback(async () => {
    if (!squareConfig?.enabled || !squareConfig.application_id) return;

    try {
      // Load Square SDK if not already loaded
      if (!window.Square) {
        const script = document.createElement('script');
        script.src = squareConfig.environment === 'production'
          ? 'https://web.squarecdn.com/v1/square.js'
          : 'https://sandbox.web.squarecdn.com/v1/square.js';
        script.async = true;
        await new Promise((resolve, reject) => {
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
      }

      const payments = window.Square.payments(squareConfig.application_id, squareConfig.location_id);
      paymentsRef.current = payments;

      const card = await payments.card();
      await card.attach('#card-container');
      cardRef.current = card;
      setPaymentReady(true);
    } catch (err) {
      console.error('Square init error:', err);
    }
  }, [squareConfig]);

  useEffect(() => {
    if (showPaymentForm) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => initSquarePayment(), 200);
      return () => clearTimeout(timer);
    }
  }, [showPaymentForm, initSquarePayment]);

  const handleBarcodeSubmit = async (e) => {
    e.preventDefault();
    if (!barcode.trim()) return;

    try {
      const product = await api.lookupBarcode(barcode);
      const existing = cart.find(item => item.variant_id === product.variant_id);
      if (existing) {
        setCart(cart.map(item =>
          item.variant_id === product.variant_id
            ? { ...item, quantity: item.quantity + 1, total: (item.quantity + 1) * item.unit_price }
            : item
        ));
      } else {
        setCart([...cart, {
          variant_id: product.variant_id,
          variant_name: product.product_name,
          sku: product.sku,
          quantity: 1,
          unit_price: product.unit_price,
          total: product.unit_price,
          is_taxable: product.is_taxable !== false, // Inherit from product
        }]);
      }
      setBarcode('');
    } catch (_error) {
      alert('Product not found for this barcode/SKU');
    }
    barcodeInputRef.current?.focus();
  };

  // Product search for POS
  const handlePosProductSearch = (e) => {
    const value = e.target.value;
    setPosProductSearch(value);
    if (posSearchTimeoutRef.current) clearTimeout(posSearchTimeoutRef.current);
    if (value.trim().length >= 2) {
      posSearchTimeoutRef.current = setTimeout(async () => {
        try {
          const results = await api.searchProducts(value);
          setPosProductResults(results);
          setShowPosProductDropdown(true);
        } catch { setPosProductResults([]); }
      }, 250);
    } else {
      setPosProductResults([]);
      setShowPosProductDropdown(false);
    }
  };

  const addPosProduct = (product) => {
    const existing = cart.find(item => item.variant_id === product.variant_id);
    if (existing) {
      setCart(cart.map(item =>
        item.variant_id === product.variant_id
          ? { ...item, quantity: item.quantity + 1, total: (item.quantity + 1) * item.unit_price }
          : item
      ));
    } else {
      setCart([...cart, {
        variant_id: product.variant_id,
        variant_name: product.product_name,
        sku: product.sku,
        quantity: 1,
        unit_price: product.unit_price,
        total: product.unit_price,
        is_taxable: true,
      }]);
    }
    setPosProductSearch('');
    setPosProductResults([]);
    setShowPosProductDropdown(false);
    barcodeInputRef.current?.focus();
  };

  const updateQuantity = (index, delta) => {
    setCart(prev => {
      const newCart = [...prev];
      const newQty = Math.max(1, newCart[index].quantity + delta);
      newCart[index].quantity = newQty;
      newCart[index].total = newQty * newCart[index].unit_price;
      return newCart;
    });
  };

  const removeItem = (index) => {
    setCart(prev => prev.filter((_, i) => i !== index));
  };

  const _toggleItemTax = (index) => {
    setCart(prev => {
      const newCart = [...prev];
      newCart[index].is_taxable = !newCart[index].is_taxable;
      return newCart;
    });
  };

  const subtotal = cart.reduce((sum, item) => sum + item.total, 0);
  // POS is for walk-in customers (not PWO) - everything is taxable
  const taxableSubtotal = subtotal;
  const tax = taxableSubtotal * TAX_RATE;
  const total = subtotal + tax;

  const handleCheckout = () => {
    if (cart.length === 0) return;
    setShowPaymentForm(true);
  };

  const processPayment = async () => {
    if (processing) return;
    setProcessing(true);

    try {
      // RULE: Walk-in name is required
      if (!customerName || !customerName.trim()) {
        alert('Walk-in name is required. Please enter the customer name.');
        setProcessing(false);
        return;
      }

      let sourceId = null;

      // Try to tokenize card if Square is ready
      if (paymentReady && cardRef.current) {
        try {
          const tokenResult = await cardRef.current.tokenize();
          if (tokenResult.status === 'OK') {
            sourceId = tokenResult.token;
          } else {
            alert('Card error: ' + (tokenResult.errors?.[0]?.message || 'Invalid card'));
            setProcessing(false);
            return;
          }
        } catch (err) {
          console.error('Tokenize error:', err);
        }
      }

      const result = await api.processSquarePayment({
        line_items: cart.map(item => ({
          variant_id: item.variant_id,
          variant_name: item.variant_name,
          sku: item.sku,
          quantity: item.quantity,
          unit_price: item.unit_price,
          total: item.total,
          is_taxable: item.is_taxable,
        })),
        source_id: sourceId,
        customer_name: customerName.trim(),
        subtotal,
        tax,
        total,
      });

      setLastReceipt(result);
      setCart([]);
      setShowPaymentForm(false);
      setPaymentReady(false);
      clearDraft(POS_KEY);
      // R13: delete the server draft now that the sale is committed
      if (currentDraftId) { try { await deleteDraft(currentDraftId); } catch { /* non-fatal */ } }
      clearCurrentDraft();

    } catch (err) {
      alert('Payment failed: ' + err.message);
    } finally {
      setProcessing(false);
    }
  };

  const clearCart = async () => {
    if (window.confirm('Clear all items?')) {
      setCart([]);
      setShowPaymentForm(false);
      clearDraft(POS_KEY);
      if (currentDraftId) { try { await deleteDraft(currentDraftId); } catch { /* non-fatal */ } }
      clearCurrentDraft();
    }
  };

  const startNewSale = () => {
    setLastReceipt(null);
    setCustomerName('');
    barcodeInputRef.current?.focus();
  };

  return (
    <div className="min-h-screen bg-background -m-6" data-testid="pos-page">
      <div className="h-screen flex flex-col">
        {/* Header */}
        <div className="bg-card border-b border-border px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShoppingBag className="h-5 w-5 text-primary" />
            <h1 className="text-xl font-semibold">Point of Sale</h1>
          </div>
          <button onClick={() => navigate('/dashboard')} className="text-sm text-muted-foreground hover:text-foreground" data-testid="exit-pos">Exit POS</button>
        </div>

        {/* Receipt Modal */}
        {lastReceipt && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="receipt-modal">
            <div className="bg-card rounded-lg border border-border p-8 max-w-md w-full text-center">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <Check className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-2xl font-semibold mb-2">Payment Complete</h3>
              <p className="text-muted-foreground mb-4">Order {lastReceipt.order_number}</p>
              <p className="text-3xl font-bold mb-6">${lastReceipt.total?.toFixed(2)}</p>
              {lastReceipt.payment?.error && (
                <p className="text-sm text-yellow-600 mb-4">Note: Card payment pending - {lastReceipt.payment.error}</p>
              )}
              <button onClick={startNewSale}
                className="w-full py-3 bg-primary text-white rounded-lg hover:bg-primary/90 text-lg font-medium" data-testid="new-sale-btn">
                New Sale
              </button>
            </div>
          </div>
        )}

        {/* Payment Form Modal */}
        {showPaymentForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="payment-modal">
            <div className="bg-card rounded-lg border border-border p-6 max-w-lg w-full">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Payment</h3>
                <button onClick={() => { setShowPaymentForm(false); setPaymentReady(false); }} className="p-1 hover:bg-muted rounded">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Walk-in Name <span className="text-red-500">*</span></label>
                <input type="text" value={customerName} onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="Enter customer name" className="w-full px-3 py-2 border border-input rounded-md" data-testid="customer-name-input" required />
                {!customerName?.trim() && <p className="text-xs text-red-500 mt-1">Required — enter the walk-in customer name</p>}
              </div>

              <div className="bg-muted/30 rounded-lg p-4 mb-4 space-y-2">
                <div className="flex justify-between text-sm"><span>Subtotal</span><span className="tabular-nums">${subtotal.toFixed(2)}</span></div>
                <div className="flex justify-between text-sm"><span>Tax (7.25%)</span><span className="tabular-nums">${tax.toFixed(2)}</span></div>
                <div className="flex justify-between text-lg font-semibold pt-2 border-t border-border">
                  <span>Total</span><span className="tabular-nums">${total.toFixed(2)}</span>
                </div>
              </div>

              {squareConfig?.enabled ? (
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">Credit Card</label>
                  <div id="card-container" className="min-h-[50px] border border-input rounded-md p-2" data-testid="square-card-container"></div>
                  {!paymentReady && <p className="text-xs text-muted-foreground mt-1">Loading payment form...</p>}
                </div>
              ) : (
                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md text-sm text-yellow-700" data-testid="square-not-configured">
                  Square payments not configured. Order will be saved without card processing.
                </div>
              )}

              <button onClick={processPayment} disabled={processing}
                className="w-full py-3 bg-primary text-white rounded-lg hover:bg-primary/90 text-lg font-medium disabled:opacity-50 flex items-center justify-center gap-2" data-testid="process-payment-btn">
                <CreditCard className="h-5 w-5" />
                {processing ? 'Processing...' : `Charge $${total.toFixed(2)}`}
              </button>
            </div>
          </div>
        )}

        <div className="flex-1 flex overflow-hidden">
          {/* Main Area */}
          <div className="flex-1 flex flex-col">
            {/* Scanner */}
            <div className="bg-card p-4 border-b border-border">
              <form onSubmit={handleBarcodeSubmit} className="flex gap-3">
                <div className="relative flex-1 max-w-2xl">
                  <Scan className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <input ref={barcodeInputRef} type="text" placeholder="Scan barcode or enter SKU..."
                    value={barcode} onChange={(e) => setBarcode(e.target.value)} autoFocus
                    className="w-full pl-12 pr-4 py-3 border-2 border-input rounded-lg text-lg focus:border-primary focus:outline-none" data-testid="pos-barcode-input" />
                </div>
                <button type="submit" className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 text-base font-medium" data-testid="pos-add-btn">
                  Add
                </button>
                <button type="button" onClick={() => setShowScanner(true)}
                  className="px-4 py-3 border-2 border-input rounded-lg hover:bg-muted" data-testid="pos-camera-scan-btn" title="Camera Scanner">
                  <Camera className="h-5 w-5" />
                </button>
              </form>
              {/* Product Search */}
              <div className="relative mt-2 max-w-2xl" ref={posProductDropdownRef}>
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input type="text" placeholder="Or search by product name..."
                  value={productSearch} onChange={handlePosProductSearch}
                  className="w-full pl-11 pr-4 py-2.5 border border-input rounded-lg focus:border-primary focus:outline-none" data-testid="pos-product-search" />
                {showPosProductDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-lg shadow-lg z-50 max-h-[28rem] overflow-y-auto" data-testid="pos-product-dropdown">
                    {productResults.length > 0 ? (
                      <>
                        <div className="sticky top-0 bg-muted/80 backdrop-blur px-3 py-1.5 text-xs text-muted-foreground border-b border-border z-10" data-testid="pos-product-results-count">
                          {productResults.length} match{productResults.length === 1 ? '' : 'es'} — scroll to see all, or type more to narrow
                        </div>
                        {productResults.map((p) => (
                      <button key={p.variant_id} onClick={() => addPosProduct(p)}
                        className="w-full text-left px-4 py-2.5 hover:bg-muted transition-colors border-b border-border last:border-b-0">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-sm">{p.product_name}</p>
                            <p className="text-xs text-muted-foreground font-mono">SKU: {p.sku}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold">${(p.unit_price || 0).toFixed(2)}</p>
                            <p className="text-xs text-muted-foreground">Qty: {p.quantity_on_hand}</p>
                          </div>
                        </div>
                      </button>
                        ))}
                        {productResults.length >= 500 && (
                          <div className="px-3 py-2 text-xs text-amber-700 bg-amber-50 border-t border-amber-200 text-center">
                            500+ matches — type more letters to narrow down
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="p-3 text-sm text-muted-foreground text-center">No products found</div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Cart */}
            <div className="flex-1 overflow-auto p-4">
              {cart.length === 0 ? (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center max-w-md w-full">
                    <Scan className="h-16 w-16 mx-auto text-muted-foreground/40 mb-3" />
                    <p className="text-lg text-muted-foreground mb-4">Scan items to start a sale</p>
                    {/* R13: Show saved server drafts here so the user can resume one instead of starting fresh */}
                    <div className="text-left">
                      <DraftsPanel
                        drafts={serverDrafts}
                        onResume={handleResumeDraft}
                        onDelete={deleteDraft}
                        currentDraftId={currentDraftId}
                        title="Saved POS Drafts"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  {cart.map((item, index) => (
                    <div key={index} className="bg-card p-3 rounded-lg border border-border flex items-center gap-3" data-testid={`cart-item-${index}`}>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{item.variant_name}</p>
                        <p className="text-xs text-muted-foreground font-mono">{item.sku}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => updateQuantity(index, -1)} className="p-1 hover:bg-muted rounded"><Minus className="h-4 w-4" /></button>
                        <span className="w-10 text-center font-medium tabular-nums">{item.quantity}</span>
                        <button onClick={() => updateQuantity(index, 1)} className="p-1 hover:bg-muted rounded"><Plus className="h-4 w-4" /></button>
                      </div>
                      <div className="text-right w-24">
                        <p className="text-xs text-muted-foreground">${item.unit_price.toFixed(2)} ea</p>
                        <p className="font-semibold tabular-nums">${item.total.toFixed(2)}</p>
                      </div>
                      <button onClick={() => removeItem(index)} className="p-1 hover:bg-red-50 rounded text-red-400 hover:text-red-600">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="w-80 bg-card border-l border-border flex flex-col">
            <div className="p-5 border-b border-border">
              <h3 className="text-base font-semibold mb-3">Order Summary</h3>
              <div className="space-y-2">
                <div className="flex justify-between"><span className="text-muted-foreground">Items</span><span className="font-medium">{cart.reduce((s, i) => s + i.quantity, 0)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-medium tabular-nums">${subtotal.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Tax (7.25%)</span><span className="font-medium tabular-nums">${tax.toFixed(2)}</span></div>
                <div className="pt-2 border-t-2 border-border flex justify-between">
                  <span className="text-lg font-semibold">Total</span>
                  <span className="text-2xl font-bold tabular-nums">${total.toFixed(2)}</span>
                </div>
              </div>
            </div>
            <div className="flex-1" />
            <div className="p-5 space-y-2">
              <button onClick={handleCheckout} disabled={cart.length === 0}
                className="w-full py-3 bg-primary text-white rounded-lg hover:bg-primary/90 text-base font-semibold disabled:opacity-50 flex items-center justify-center gap-2" data-testid="checkout-btn">
                <CreditCard className="h-5 w-5" /> Checkout
              </button>
              <button onClick={clearCart} disabled={cart.length === 0}
                className="w-full py-2 border border-border rounded-lg hover:bg-muted text-sm font-medium disabled:opacity-50" data-testid="clear-cart-btn">
                Clear Cart
              </button>
            </div>
          </div>
        </div>
      </div>

    {/* Camera Barcode Scanner Modal */}
    {showScanner && (
      <BarcodeScanner
        onScan={async (code) => {
          setShowScanner(false);
          try {
            const product = await api.lookupBarcode(code);
            const existing = cart.find(item => item.variant_id === product.variant_id);
            if (existing) {
              setCart(prev => prev.map(item =>
                item.variant_id === product.variant_id
                  ? { ...item, quantity: item.quantity + 1, total: (item.quantity + 1) * item.unit_price }
                  : item
              ));
            } else {
              setCart(prev => [...prev, {
                variant_id: product.variant_id,
                variant_name: product.product_name,
                sku: product.sku,
                quantity: 1,
                unit_price: product.unit_price,
                total: product.unit_price,
                is_taxable: true,
              }]);
            }
          } catch {
            alert('Product not found for this barcode/SKU: ' + code);
          }
        }}
        onClose={() => setShowScanner(false)}
      />
    )}
    </div>
  );
};

export default POS;
